import { useEffect, useState } from "react";
import { ScrollText, CheckCircle2, Sparkles, Info } from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const INR = (n) =>
  `Rs. ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const INR0 = (n) =>
  `Rs. ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function DailyReferralBonus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/daily-referral-bonus");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatDT = (iso) => {
    if (!iso) return { d: "—", t: "" };
    try {
      const dt = new Date(iso);
      return {
        d: dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
        t: dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
      };
    } catch {
      return { d: "—", t: "" };
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-5 text-slate-900">
        <section className="bg-white rounded-2xl shadow-lg p-6">
          <h1
            className="text-2xl sm:text-3xl font-black tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Daily Referral Bonus
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Once your rank reaches <b>Promoter</b>, you earn Rs. 10 daily for 200 days
            — auto-credited to your E-Wallet.
          </p>
        </section>

        {loading || !data ? (
          <div className="py-16 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-white/40 border-t-white animate-spin" />
          </div>
        ) : !data.eligible ? (
          <section
            data-testid="daily-bonus-locked"
            className="bg-white rounded-2xl shadow-lg p-6 text-center"
          >
            <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Info size={22} />
            </div>
            <h2 className="mt-3 text-lg font-extrabold text-slate-900">
              Reach Promoter rank to unlock
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Your current rank is <b>{data.rank}</b>. Sponsor 2 Executive referrals to
              become a Promoter and start earning ₹10 every day for 200 days.
            </p>
          </section>
        ) : (
          <>
            {/* Summary card */}
            <section
              data-testid="daily-bonus-summary"
              className="rounded-2xl p-6 text-white shadow-xl grid grid-cols-1 sm:grid-cols-3 gap-6"
              style={{ background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)" }}
            >
              <div>
                <div className="text-sm text-white/85">Total Credited</div>
                <div
                  className="mt-1 text-4xl font-black tracking-tight"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                  data-testid="bonus-total-credited"
                >
                  {INR0(data.total_credited)}
                </div>
                <div className="text-xs text-white/80 mt-1">
                  Rs. {data.daily_amount} × {data.days_credited} days
                </div>
              </div>
              <div>
                <div className="text-sm text-white/85">Days Credited</div>
                <div
                  className="mt-1 text-4xl font-black tracking-tight"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                  data-testid="bonus-days-credited"
                >
                  {data.days_credited}
                  <span className="text-lg font-semibold text-white/80"> / {data.total_days}</span>
                </div>
                <div className="text-xs text-white/80 mt-1">
                  {data.days_remaining} days remaining
                </div>
              </div>
              <div>
                <div className="text-sm text-white/85">Auto Credit</div>
                <div
                  className="mt-1 text-lg font-black tracking-tight flex items-center gap-2"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  <Sparkles size={20} className="text-yellow-200" /> E-Wallet
                </div>
                <div className="text-xs text-white/80 mt-1">
                  Started {data.start_date ? new Date(data.start_date).toLocaleDateString("en-IN") : "—"}
                </div>
              </div>
            </section>

            {/* Progress bar */}
            <section className="bg-white rounded-2xl shadow-lg p-5">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-slate-700">Progress</span>
                <span className="text-slate-500 tabular-nums">
                  {Math.round((data.days_credited / data.total_days) * 100)}%
                </span>
              </div>
              <div className="mt-2 h-3 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-red-600 transition-all"
                  style={{ width: `${Math.min(100, (data.days_credited / data.total_days) * 100)}%` }}
                />
              </div>
            </section>

            {/* History */}
            <section
              data-testid="daily-bonus-history"
              className="bg-white rounded-2xl shadow-lg overflow-hidden"
            >
              <div className="px-5 pt-5 pb-3">
                <h2
                  className="text-xl font-extrabold flex items-center gap-2"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  <ScrollText size={18} className="text-red-700" />
                  Daily Referral Bonus History
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  All bonuses are auto-credited to your E-Wallet.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest font-bold">
                    <tr>
                      <th className="text-left px-4 py-3">Sr. No.</th>
                      <th className="text-left px-4 py-3">Amount</th>
                      <th className="text-left px-4 py-3">Date &amp; Time</th>
                      <th className="text-left px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody data-testid="daily-bonus-table" className="divide-y divide-slate-100">
                    {data.history.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-10 text-slate-500">
                          No bonuses credited yet. Come back tomorrow!
                        </td>
                      </tr>
                    ) : (
                      data.history
                        .slice()
                        .reverse()
                        .map((r) => {
                          const dt = formatDT(r.created_at);
                          return (
                            <tr key={r.id} className="hover:bg-slate-50">
                              <td className="px-4 py-3 font-semibold text-slate-800">
                                {r.sr_no}
                              </td>
                              <td className="px-4 py-3 font-extrabold text-emerald-600 tabular-nums">
                                {INR(r.amount)}
                              </td>
                              <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                                <div className="font-semibold">{dt.d}</div>
                                <div className="text-xs text-slate-500">{dt.t || "12:00 AM"}</div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 text-xs font-semibold px-2.5 py-1 rounded-full">
                                  <CheckCircle2 size={12} /> Credited
                                </span>
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-600 text-center">
                Showing <b>{data.history.length}</b> record{data.history.length === 1 ? "" : "s"}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
